import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import StatsService from "../../services/doctorServices/StatsService";
import StatsChart from "./StatsChart";

const CHART_TIME_OPTIONS = [
  { value: "last_week", label: "Son 1 Hafta" },
  { value: "last_month", label: "Son 1 Ay" },
  { value: "last_3_months", label: "Son 3 Ay" },
  { value: "last_6_months", label: "Son 6 Ay" },
  { value: "last_year", label: "Son 1 Yıl" },
];
const CHART_GROUP_OPTIONS = [
  { value: "day", label: "Gün" },
  { value: "week", label: "Hafta" },
  { value: "month", label: "Ay" },
];

const CHART_ENTITY_OPTIONS = [
  "patients",
  "forms",
  "form_answers",
  "form_invitations",
  "files",
  "scoring_formulas",
];
const METRICS_BY_ENTITY = {
  patients: ["count", "new_count"],
  forms: ["count", "new_count"],
  form_answers: ["count", "new_count"],
  form_invitations: ["count", "sent_count", "sent_count_cumulative", "completed_count", "pending_count"],
  files: ["count", "new_count", "with_forms"],
  scoring_formulas: ["count", "new_count", "in_use_count"],
};

const DOCTOR_NO_RESULTS_OPTION = { __noResults: true, id: "__no_results__" };

function chartDoctorInitials(label) {
  if (!label || typeof label !== "string") return "?";
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase() || "?";
  }
  return parts[0].slice(0, 2).toUpperCase() || "?";
}

function DoctorChartDropdownListbox({ listboxProps, searchValue, onSearchChange, title, placeholder, colors, panelRef }) {
  return (
    <Paper
      ref={panelRef}
      elevation={0}
      component="div"
      sx={{
        maxHeight: 380,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        mt: 1,
        background: `linear-gradient(165deg, ${colors.primary[600]} 0%, ${colors.primary[500]} 100%)`,
        border: `1px solid ${colors.blueAccent[500]}55`,
        boxShadow: `0 20px 48px rgba(0, 0, 0, 0.45), 0 0 0 1px ${colors.blueAccent[700]}33 inset`,
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1,
          flexShrink: 0,
          borderBottom: `1px solid ${colors.primary[400]}66`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
              boxShadow: `0 4px 14px ${colors.blueAccent[700]}55`,
            }}
          >
            <PersonOutlineIcon sx={{ color: colors.grey[100], fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: colors.grey[400], letterSpacing: 0.6, fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
        </Stack>
        <TextField
          size="small"
          fullWidth
          placeholder={placeholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.blueAccent[400], fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: `${colors.primary[700]}aa`,
              borderRadius: 2,
              color: colors.grey[100],
              "& fieldset": { borderColor: `${colors.blueAccent[500]}66` },
              "&:hover fieldset": { borderColor: colors.blueAccent[400] },
              "&.Mui-focused fieldset": {
                borderColor: colors.blueAccent[300],
                boxShadow: `0 0 0 3px ${colors.blueAccent[500]}22`,
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: colors.grey[500],
              opacity: 1,
            },
          }}
        />
      </Box>
      <List
        {...listboxProps}
        sx={{
          overflow: "auto",
          flex: 1,
          py: 1,
          px: 0.5,
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: `${colors.blueAccent[700]}99`,
            borderRadius: 4,
          },
          "& .MuiAutocomplete-option": {
            px: 1,
            py: 0.5,
            mx: 0.5,
            mb: 0.25,
            borderRadius: 2,
            color: colors.grey[100],
            transition: "background-color 0.15s ease",
            "&:hover": {
              backgroundColor: `${colors.blueAccent[800]}55`,
            },
            '&[aria-selected="true"]': {
              backgroundColor: `${colors.blueAccent[700]}44`,
            },
            "&.Mui-focused": {
              backgroundColor: `${colors.blueAccent[800]}66`,
            },
          },
        }}
      />
    </Paper>
  );
}

/**
 * @param {Array<{ id: string|number, label: string }>} doctorOptions
 * @param {(params: object) => Promise<object>} fetchStats — getTenantStats / getDoctorStats ile aynı query; API gövdesini döndürür
 * @param {object} [sx] — dış konteyner Box sx (ör. maxWidth genişletme)
 */
export default function DoctorPerformanceChartPanel({ doctorOptions, fetchStats, sx: panelSx }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation(["tenantDashboard", "stats", "team"]);
  const statsService = useMemo(() => new StatsService(), []);

  /** Varlık / metrik / zaman / grup Select menüleri — doktor Autocomplete paneli ile aynı görsel dil */
  const chartSelectMenuProps = useMemo(
    () => ({
      disableAutoFocusItem: true,
      anchorOrigin: { vertical: "bottom", horizontal: "left" },
      transformOrigin: { vertical: "top", horizontal: "left" },
      PaperProps: {
        elevation: 0,
        sx: {
          mt: 0.75,
          borderRadius: 3,
          maxHeight: 320,
          overflow: "auto",
          background: `linear-gradient(165deg, ${colors.primary[600]} 0%, ${colors.primary[500]} 100%)`,
          border: `1px solid ${colors.blueAccent[500]}55`,
          boxShadow: `0 20px 48px rgba(0, 0, 0, 0.45), 0 0 0 1px ${colors.blueAccent[700]}33 inset`,
          zIndex: (muiTheme) => muiTheme.zIndex.modal,
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: `${colors.blueAccent[700]}99`,
            borderRadius: 4,
          },
          "& .MuiMenu-list": {
            py: 1,
            px: 0.5,
          },
          "& .MuiMenuItem-root": {
            borderRadius: 2,
            mx: 0.5,
            my: 0.25,
            py: 1,
            px: 1.5,
            fontSize: "0.875rem",
            fontWeight: 500,
            color: colors.grey[100],
            transition: "background-color 0.15s ease",
            "&:hover": {
              backgroundColor: `${colors.blueAccent[800]}55`,
            },
            "&.Mui-selected": {
              backgroundColor: `${colors.blueAccent[700]}44`,
              "&:hover": {
                backgroundColor: `${colors.blueAccent[700]}55`,
              },
            },
            "&.Mui-focusVisible": {
              backgroundColor: `${colors.blueAccent[800]}66`,
            },
          },
        },
      },
    }),
    [colors]
  );

  const chartSelectTriggerSx = useMemo(
    () => ({
      color: colors.grey[100],
      backgroundColor: colors.primary[600],
      borderRadius: 2,
      transition: "box-shadow 0.2s ease, border-color 0.2s ease",
      "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        backgroundColor: colors.primary[600],
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: `${colors.blueAccent[500]}88`,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.blueAccent[400],
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.blueAccent[300],
        boxShadow: `0 0 0 3px ${colors.blueAccent[500]}22`,
      },
      "& .MuiSelect-icon": {
        color: colors.blueAccent[400],
        transition: "color 0.2s ease, transform 0.2s ease",
      },
      "& .MuiSelect-iconOpen": {
        transform: "rotate(180deg)",
        color: colors.blueAccent[300],
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiSelect-icon": {
        color: colors.blueAccent[300],
      },
    }),
    [colors]
  );

  const chartFilterInputLabelSx = useMemo(
    () => ({
      color: colors.grey[300],
      "&.Mui-focused": { color: colors.blueAccent[400] },
      "&.MuiInputLabel-shrink": { letterSpacing: 0.3 },
    }),
    [colors]
  );

  const [selectedDoctorOptions, setSelectedDoctorOptions] = useState([]);
  const [chartTimeFilter, setChartTimeFilter] = useState("last_month");
  const [chartGroupBy, setChartGroupBy] = useState("day");
  const [chartEntity, setChartEntity] = useState("patients");
  const [chartMetric, setChartMetric] = useState("count");
  const [doctorStatsData, setDoctorStatsData] = useState(null);
  const [doctorStatsLoading, setDoctorStatsLoading] = useState(false);
  const [doctorStatsError, setDoctorStatsError] = useState(null);
  const [doctorSearchInDropdown, setDoctorSearchInDropdown] = useState("");
  const [doctorDropdownOpen, setDoctorDropdownOpen] = useState(false);
  const doctorDropdownPanelRef = useRef(null);
  const doctorAutocompleteRootRef = useRef(null);

  /** Portallı liste dışına tıklanınca kapanır; iç arama alanı / seçenekler panel ref içinde kalır */
  useEffect(() => {
    if (!doctorDropdownOpen) return;
    const onMouseDownCapture = (event) => {
      const target = event.target;
      if (doctorDropdownPanelRef.current?.contains(target)) return;
      if (doctorAutocompleteRootRef.current?.contains(target)) return;
      setDoctorDropdownOpen(false);
      setDoctorSearchInDropdown("");
    };
    document.addEventListener("mousedown", onMouseDownCapture, true);
    return () => document.removeEventListener("mousedown", onMouseDownCapture, true);
  }, [doctorDropdownOpen]);

  const DoctorListboxComponent = useCallback(
    (listboxProps) => (
      <DoctorChartDropdownListbox
        listboxProps={listboxProps}
        searchValue={doctorSearchInDropdown}
        onSearchChange={setDoctorSearchInDropdown}
        title={t("searchDoctorsForChart")}
        placeholder={t("searchDoctors")}
        colors={colors}
        panelRef={doctorDropdownPanelRef}
      />
    ),
    [t, colors, doctorSearchInDropdown]
  );

  const fetchDoctorStats = useCallback(async () => {
    if (selectedDoctorOptions.length === 0) {
      setDoctorStatsData(null);
      return;
    }
    setDoctorStatsLoading(true);
    setDoctorStatsError(null);
    try {
      const range = statsService.getDateRange(chartTimeFilter);
      const groupBy =
        chartGroupBy === "none" ? statsService.getSuggestedGroupBy(chartTimeFilter) : chartGroupBy;
      const doctorIds = selectedDoctorOptions.map((o) => o.id).join(",");
      const response = await fetchStats({
        entity: chartEntity,
        doctor_id: doctorIds,
        metric: chartMetric,
        start_date: range.startDate,
        end_date: range.endDate,
        group_by: groupBy,
      });
      const entityData = response?.data?.[chartEntity];
      if (entityData?.doctors) {
        const doctors = entityData.doctors;
        const chartData = {};
        const entities = [];
        const entityLabels = {};
        doctors.forEach((d) => {
          if (d.error) return;
          const rawId = d.doctor?.id ?? d.doctor;
          const id = rawId != null ? String(rawId) : "";
          if (!id) return;
          const series = Array.isArray(d.series) ? d.series : [];
          const namePart = `${d.doctor?.name || ""} ${d.doctor?.surname || ""}`.trim();
          const fullName = (d.doctor?.full_name ?? namePart) || id;
          chartData[id] = { series };
          entities.push(id);
          entityLabels[id] = fullName;
        });
        if (entities.length === 0) {
          setDoctorStatsData(null);
          return;
        }
        const firstPeriod = doctors[0]?.period;
        setDoctorStatsData({
          data: chartData,
          entities,
          entityLabels,
          period: firstPeriod || { start_date: range.startDate, end_date: range.endDate, group_by: groupBy },
        });
      } else {
        setDoctorStatsData(null);
      }
    } catch (err) {
      console.error("Doctor stats error:", err);
      setDoctorStatsError(err.response?.data?.message || err.message || "Failed to load stats");
      setDoctorStatsData(null);
    } finally {
      setDoctorStatsLoading(false);
    }
  }, [selectedDoctorOptions, chartTimeFilter, chartGroupBy, chartEntity, chartMetric, statsService, fetchStats]);

  useEffect(() => {
    const allowed = METRICS_BY_ENTITY[chartEntity] || ["count"];
    if (!allowed.includes(chartMetric)) setChartMetric(allowed[0]);
  }, [chartEntity, chartMetric]);

  useEffect(() => {
    if (selectedDoctorOptions.length > 0) fetchDoctorStats();
    else setDoctorStatsData(null);
  }, [selectedDoctorOptions, chartTimeFilter, chartGroupBy, chartEntity, chartMetric, fetchDoctorStats]);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1200px",
        margin: "0 auto",
        background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
        borderRadius: "16px",
        border: `1px solid ${colors.grey[800]}30`,
        p: { xs: 2, md: 3 },
        ...panelSx,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <BarChartOutlinedIcon sx={{ color: colors.grey[100], fontSize: 28 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.grey[100], letterSpacing: 0.4 }}>
          {t("doctorPerformanceChart")}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: colors.grey[400], mb: 2, pl: 5 }}>
        {t("doctorChartShowsLabel")} {t(`stats:entities.${chartEntity}`)} – {t(`stats:metrics.${chartMetric}`)}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }} alignItems="flex-start" flexWrap="wrap">
        <Box ref={doctorAutocompleteRootRef} sx={{ minWidth: 280, flex: 1, alignSelf: { xs: "stretch", sm: "auto" } }}>
        <Autocomplete
          multiple
          open={doctorDropdownOpen}
          onOpen={() => setDoctorDropdownOpen(true)}
          onClose={(e, reason) => {
            if (reason === "blur") {
              setTimeout(() => {
                if (doctorDropdownPanelRef.current?.contains(document.activeElement)) return;
                setDoctorDropdownOpen(false);
                setDoctorSearchInDropdown("");
              }, 0);
              return;
            }
            setDoctorDropdownOpen(false);
            setDoctorSearchInDropdown("");
          }}
          options={doctorOptions}
          getOptionLabel={(opt) => (opt.__noResults ? t("noDoctorsFoundForChart") : opt.label)}
          value={selectedDoctorOptions}
          onChange={(_, newValue) => setSelectedDoctorOptions(newValue.filter((x) => !x.__noResults))}
          isOptionEqualToValue={(opt, val) => !opt.__noResults && opt.id === val.id}
          getOptionDisabled={(opt) => !!opt.__noResults}
          filterOptions={(options, { inputValue }) => {
            const q = (doctorSearchInDropdown || inputValue || "").trim().toLowerCase();
            if (!q) return options;
            const filtered = options.filter((o) => (o.label || "").toLowerCase().includes(q));
            return filtered.length === 0 ? [DOCTOR_NO_RESULTS_OPTION] : filtered;
          }}
          renderOption={(props, opt) =>
            opt.__noResults ? (
              <li {...props} key={opt.id} style={{ cursor: "default" }}>
                <Typography variant="body2" sx={{ color: colors.grey[400], fontStyle: "italic", py: 0.5, px: 1 }}>
                  {t("noDoctorsFoundForChart")}
                </Typography>
              </li>
            ) : (
              <li {...props} key={opt.id} style={{ padding: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    py: 0.75,
                    px: 1,
                    width: "100%",
                    minHeight: 44,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      background: `linear-gradient(145deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
                      color: colors.grey[100],
                      boxShadow: `0 2px 8px ${colors.primary[900]}66`,
                    }}
                  >
                    {chartDoctorInitials(opt.label)}
                  </Avatar>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.grey[100],
                      fontWeight: 500,
                      lineHeight: 1.35,
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {opt.label}
                  </Typography>
                </Box>
              </li>
            )
          }
          ListboxComponent={DoctorListboxComponent}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                size="small"
                avatar={
                  <Avatar
                    sx={{
                      width: 26,
                      height: 26,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      background: `linear-gradient(145deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
                      color: colors.grey[100],
                      boxShadow: `0 1px 4px ${colors.primary[900]}55`,
                    }}
                  >
                    {chartDoctorInitials(option.label)}
                  </Avatar>
                }
                label={option.label}
                sx={{
                  height: "auto",
                  py: 0.4,
                  pl: 0.25,
                  pr: 0.25,
                  borderRadius: 2,
                  border: `1px solid ${colors.blueAccent[500]}55`,
                  background: `linear-gradient(135deg, ${colors.primary[700]}ee 0%, ${colors.primary[600]}cc 100%)`,
                  color: colors.grey[100],
                  "& .MuiChip-label": {
                    px: 0.75,
                    py: 0.15,
                    maxWidth: { xs: 160, sm: 220 },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                    fontSize: "0.8125rem",
                  },
                  "& .MuiChip-deleteIcon": {
                    color: colors.grey[400],
                    fontSize: 18,
                    marginRight: 4,
                    "&:hover": { color: colors.grey[100], backgroundColor: `${colors.blueAccent[700]}44`, borderRadius: 1 },
                  },
                }}
              />
            ))
          }
          sx={{ width: "100%" }}
          slotProps={{
            popper: {
              placement: "bottom-start",
              modifiers: [{ name: "flip", enabled: true }, { name: "preventOverflow", enabled: true }],
              sx: { zIndex: (theme) => theme.zIndex.modal },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("searchDoctorsForChart")}
              placeholder={t("searchDoctors")}
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: colors.grey[400], fontSize: 20 }} />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.primary[600],
                  borderRadius: 2,
                  flexWrap: "wrap",
                  gap: 0.5,
                  py: 0.75,
                  alignItems: "center",
                  "& fieldset": { borderColor: colors.blueAccent[500] },
                  "&:hover fieldset": { borderColor: colors.blueAccent[400] },
                },
                "& .MuiAutocomplete-inputRoot": {
                  flexWrap: "wrap",
                  gap: 0.5,
                },
                "& .MuiInputLabel-root": { color: colors.grey[300] },
                "& .MuiInputBase-input": { color: colors.grey[100], minWidth: 120 },
              }}
            />
          )}
        />
        </Box>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={chartFilterInputLabelSx}>{t("stats:entity")}</InputLabel>
          <Select
            value={chartEntity}
            label={t("stats:entity")}
            onChange={(e) => setChartEntity(e.target.value)}
            MenuProps={chartSelectMenuProps}
            sx={chartSelectTriggerSx}
          >
            {CHART_ENTITY_OPTIONS.map((ent) => (
              <MenuItem key={ent} value={ent}>
                {t(`stats:entities.${ent}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel sx={chartFilterInputLabelSx}>{t("stats:metric")}</InputLabel>
          <Select
            value={chartMetric}
            label={t("stats:metric")}
            onChange={(e) => setChartMetric(e.target.value)}
            MenuProps={chartSelectMenuProps}
            sx={chartSelectTriggerSx}
          >
            {(METRICS_BY_ENTITY[chartEntity] || ["count"]).map((m) => (
              <MenuItem key={m} value={m}>
                {t(`stats:metrics.${m}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel sx={chartFilterInputLabelSx}>{t("stats:timePeriod")}</InputLabel>
          <Select
            value={chartTimeFilter}
            label={t("stats:timePeriod")}
            onChange={(e) => setChartTimeFilter(e.target.value)}
            MenuProps={chartSelectMenuProps}
            sx={chartSelectTriggerSx}
          >
            {CHART_TIME_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel sx={chartFilterInputLabelSx}>{t("stats:groupBy")}</InputLabel>
          <Select
            value={chartGroupBy}
            label={t("stats:groupBy")}
            onChange={(e) => setChartGroupBy(e.target.value)}
            MenuProps={chartSelectMenuProps}
            sx={chartSelectTriggerSx}
          >
            {CHART_GROUP_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      {selectedDoctorOptions.length === 0 ? (
        <Typography sx={{ color: colors.grey[400], py: 4, textAlign: "center" }}>
          {t("selectDoctorsToShowChart")}
        </Typography>
      ) : (
        <StatsChart
          key={`${chartEntity}-${chartMetric}`}
          data={doctorStatsData?.data}
          loading={doctorStatsLoading}
          error={doctorStatsError}
          metric={chartMetric}
          entity={chartEntity}
          entities={doctorStatsData?.entities ?? []}
          entityLabels={doctorStatsData?.entityLabels}
          period={chartTimeFilter}
          groupBy={
            chartGroupBy === "none" ? statsService.getSuggestedGroupBy(chartTimeFilter) : chartGroupBy
          }
        />
      )}
    </Box>
  );
}
